import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { BeratungSeite } from "@/hooks/useBeratungSeiten";
import {
  PriorityTiles,
  PriorityTilesEditor,
} from "../visualizations";

interface KundenAnsichtCardProps {
  /** The current page/step data */
  seite: BeratungSeite | null;
  /** Form values from the session */
  formValues: Record<string, any>;
  /** Whether tiles are interactive (clickable) */
  interactiveTiles?: boolean;
  /** Callback when a tile is clicked */
  onTileSelect?: (tileId: string) => void;
  /** Currently hovered tile ID for cross-view sync */
  hoveredTileId?: string | null;
  /** Callback when tile hover changes */
  onTileHover?: (tileId: string | null) => void;
  /** Whether edit mode is active */
  isEditModeActive?: boolean;
  /** Callback when tiles are updated in edit mode */
  onTilesUpdate?: (tiles: any[]) => void;
  /** Additional className */
  className?: string;
}

/**
 * Reusable Kunden-Ansicht card for priority tiles display.
 * Shows the "Welche Bereiche sind Ihnen wichtig?" view.
 */
export function KundenAnsichtCard({
  seite,
  formValues,
  interactiveTiles = false,
  onTileSelect,
  hoveredTileId,
  onTileHover,
  isEditModeActive = false,
  onTilesUpdate,
  className,
}: KundenAnsichtCardProps) {
  if (!seite) {
    return (
      <Card className={cn("border-none shadow-lg", className)}>
        <CardContent className="flex items-center justify-center h-48 text-muted-foreground">
          Keine Seite ausgewählt
        </CardContent>
      </Card>
    );
  }

  const layout = seite.kunden_layout;

  // Only render for priority-tiles type
  if (layout?.type !== 'priority-tiles') {
    return null;
  }

  // Extract selected priorities from formValues
  const getSelectedPriorities = (): string[] => {
    const priorityKeys = Object.keys(formValues).filter(
      key => key.toLowerCase().includes('priorit') || key.toLowerCase().includes('priority')
    );
    const selected: string[] = [];
    priorityKeys.forEach(key => {
      const val = formValues[key];
      if (Array.isArray(val)) {
        selected.push(...val);
      } else if (val) {
        selected.push(val);
      }
    });
    return selected;
  };

  const tiles = layout.tiles || [];
  const title = layout.title || "Welche Bereiche sind Ihnen wichtig?";
  const subtitle = layout.subtitle || "Ihr maßgeschneidertes Konzept.";

  return (
    <div className={cn("p-6", className)}>
      {isEditModeActive ? (
        <PriorityTilesEditor
          tiles={tiles}
          onChange={(updatedTiles) => onTilesUpdate?.(updatedTiles)}
          title={title}
          subtitle={subtitle}
        />
      ) : (
        <div className="space-y-6">
          {/* Left-aligned Title and Subtitle - matches RentTimelineChart and ChildProvisionChart */}
          <div className="text-left">
            <h2 className="text-2xl md:text-3xl font-bold">
              <span className="text-foreground">Welche Bereiche </span>
              <span className="text-primary">sind Ihnen wichtig?</span>
            </h2>
            <p className="text-lg italic text-muted-foreground mt-1">
              {subtitle}
            </p>
          </div>
          <PriorityTiles
            tiles={tiles}
            selectedIds={getSelectedPriorities()}
            animated={layout.animated !== false}
            interactive={interactiveTiles}
            onTileClick={onTileSelect}
            hoveredTileId={hoveredTileId}
            onTileHover={onTileHover}
          />
        </div>
      )}
    </div>
  );
}
